import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { Mail, Loader2, CheckCircle2, XCircle, ArrowLeft, ChevronsUpDown } from "lucide-react";
import { useRoninAuthGate } from "@/components/useRoninAuthGate";

/**
 * Sign-up / log-in gate for Ronin Box. Renders a 2-mode dialog:
 *   - "signup": full Better Through Boxing program-style form + required marketing consent
 *   - "login":  existing-account email + password
 *
 * After a successful signup, shows a "Check your email" screen with a Resend button.
 * Calling onAccessGranted() means the user is either verified, an admin, or has prior bookings.
 *
 * All state/handlers live in the `useRoninAuthGate` hook; this file is presentational only.
 */
export function RoninAuthGate({ open, onClose, onAccessGranted, initialMode = "signup" }) {
  const g = useRoninAuthGate({ initialMode, onAccessGranted, onClose });

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !g.busy && onClose?.()}>
      <DialogContent className="bg-[#0A0A0A] border-[#27272A] text-white max-w-lg max-h-[92vh] overflow-y-auto" data-testid="ronin-auth-gate">
        {g.submitted ? (
          <VerifyEmailView submittedEmail={g.submittedEmail} busy={g.busy} resendCooldown={g.resendCooldown} resend={g.resend} onClose={onClose} />
        ) : g.mode === "signup" ? (
          <SignupForm gate={g} />
        ) : (
          <LoginForm gate={g} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function VerifyEmailView({ submittedEmail, busy, resendCooldown, resend, onClose }) {
  return (
    <div data-testid="ronin-auth-verify">
      <DialogHeader>
        <DialogTitle className="text-white uppercase tracking-tight flex items-center gap-2">
          <Mail className="h-5 w-5 text-[#FF331F]" /> Check your email
        </DialogTitle>
      </DialogHeader>
      <p className="text-sm text-zinc-400 mt-3 leading-relaxed">
        We just sent a verification link to <strong className="text-white">{submittedEmail}</strong>. Click it and you&apos;re in — Ronin Box unlocks the second you verify.
      </p>
      <div className="mt-4 border border-[#27272A] bg-[#050505] rounded-sm p-4">
        <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-1">Didn&apos;t see it?</p>
        <p className="text-xs text-zinc-400 mb-3">Check your spam folder. Promotions / Updates tab in Gmail. Make sure your email was typed right.</p>
        <Button onClick={resend} disabled={busy || resendCooldown > 0}
          variant="outline" className="border-[#27272A] bg-[#0F0F0F] text-zinc-200 hover:text-white text-xs uppercase tracking-widest font-bold w-full"
          data-testid="ronin-auth-resend">
          {busy ? <><Loader2 className="h-3 w-3 mr-2 animate-spin" /> Sending…</>
            : resendCooldown > 0 ? `Resend in ${resendCooldown}s`
            : <><Mail className="h-3 w-3 mr-2" /> Resend the email</>}
        </Button>
      </div>
      <Button onClick={onClose} variant="ghost" className="w-full mt-3 text-zinc-500 hover:text-white text-xs uppercase tracking-widest font-bold" data-testid="ronin-auth-close-verify">Close</Button>
    </div>
  );
}

function SignupForm({ gate: g }) {
  const { form, setForm, setField, countries, countryOpen, setCountryOpen, postalState, busy, submitSignup, setMode } = g;
  return (
    <form onSubmit={submitSignup} data-testid="ronin-auth-signup">
      <DialogHeader>
        <DialogTitle className="text-white uppercase tracking-tight">Unlock Ronin Box · free</DialogTitle>
      </DialogHeader>
      <p className="text-xs text-zinc-400 mt-2 mb-4">Create a free account to use Ronin Box. Same details as if you were starting the full Better Through Boxing program — no credit card.</p>

      <div className="grid grid-cols-2 gap-3 mb-3">
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">First name *</Label>
          <Input required value={form.first_name} onChange={setField("first_name")} className="bg-[#050505] border-[#27272A] text-white rounded-sm" data-testid="ronin-auth-first" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Last name *</Label>
          <Input required value={form.last_name} onChange={setField("last_name")} className="bg-[#050505] border-[#27272A] text-white rounded-sm" data-testid="ronin-auth-last" />
        </div>
      </div>
      <div className="mb-3">
        <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Email *</Label>
        <Input required type="email" value={form.email} onChange={setField("email")} className="bg-[#050505] border-[#27272A] text-white rounded-sm" data-testid="ronin-auth-email" />
      </div>
      <div className="mb-3">
        <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Phone *</Label>
        <Input required type="tel" value={form.phone} onChange={setField("phone")} placeholder="+1 (555) 123-4567" className="bg-[#050505] border-[#27272A] text-white rounded-sm" data-testid="ronin-auth-phone" />
      </div>
      <div className="grid grid-cols-5 gap-3 mb-3">
        <div className="col-span-3">
          <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Country *</Label>
          <Popover open={countryOpen} onOpenChange={setCountryOpen}>
            <PopoverTrigger asChild>
              <Button type="button" variant="outline"
                className="w-full justify-between bg-[#050505] border-[#27272A] text-white rounded-sm hover:bg-[#0F0F0F] hover:text-white font-normal"
                data-testid="ronin-auth-country">
                {countries.find((c) => c.code === form.country)?.name || form.country}
                <ChevronsUpDown className="ml-2 h-3 w-3 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="p-0 bg-[#0A0A0A] border-[#27272A] w-[var(--radix-popover-trigger-width)]" align="start">
              <Command className="bg-[#0A0A0A]">
                <CommandInput placeholder="Type to search…" className="text-white" data-testid="ronin-auth-country-search" />
                <CommandList className="max-h-[240px]">
                  <CommandEmpty className="text-zinc-500 text-sm p-3">No country.</CommandEmpty>
                  <CommandGroup>
                    {countries.map((c) => (
                      <CommandItem key={c.code} value={`${c.name} ${c.code}`}
                        onSelect={() => { setForm({ ...form, country: c.code, zip_code: "" }); setCountryOpen(false); }}
                        className="text-white aria-selected:bg-[#FF331F]/15 cursor-pointer"
                        data-testid={`ronin-country-${c.code}`}>
                        <span className="font-mono text-[10px] text-zinc-500 w-7">{c.code}</span>
                        {c.name}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
        <div className="col-span-2">
          <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Postal / ZIP *</Label>
          <div className="relative">
            <Input required value={form.zip_code} onChange={setField("zip_code")}
              placeholder={form.country === "US" ? "10001" : form.country === "CA" ? "M5V 2T6" : form.country === "GB" ? "SW1A 1AA" : "Postal code"}
              className={`bg-[#050505] border-[#27272A] text-white rounded-sm pr-9 ${
                postalState.status === "bad" ? "border-red-500/60" :
                postalState.status === "ok" ? "border-emerald-500/60" : ""
              }`} data-testid="ronin-auth-zip" />
            <div className="absolute right-2 top-1/2 -translate-y-1/2">
              {postalState.status === "checking" && <Loader2 className="h-3.5 w-3.5 animate-spin text-zinc-500" />}
              {postalState.status === "ok" && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />}
              {postalState.status === "bad" && <XCircle className="h-3.5 w-3.5 text-red-400" />}
            </div>
          </div>
        </div>
      </div>
      {postalState.status === "ok" && postalState.place && (
        <p className="text-[11px] text-emerald-400 -mt-1 mb-3" data-testid="ronin-postal-confirm">
          <CheckCircle2 className="h-3 w-3 inline mr-1" /> {postalState.place}{postalState.region ? `, ${postalState.region}` : ""}
        </p>
      )}
      {postalState.status === "bad" && (
        <p className="text-[11px] text-red-400 -mt-1 mb-3" data-testid="ronin-postal-error">
          <XCircle className="h-3 w-3 inline mr-1" /> We couldn&apos;t find that postal code in the selected country. Double-check it.
        </p>
      )}
      <div className="mb-3">
        <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Password * <span className="normal-case tracking-normal text-zinc-600">(min 6)</span></Label>
        <Input required type="password" minLength={6} value={form.password} onChange={setField("password")} className="bg-[#050505] border-[#27272A] text-white rounded-sm" data-testid="ronin-auth-password" />
      </div>

      <label className="flex items-start gap-2 mb-2 cursor-pointer">
        <input type="checkbox" checked={form.marketing_consent} onChange={(e) => setForm({ ...form, marketing_consent: e.target.checked })}
          className="mt-0.5 h-4 w-4 accent-[#FF331F]" data-testid="ronin-auth-marketing" />
        <span className="text-[11px] text-zinc-400 leading-snug">
          <strong className="text-white">Required:</strong> Send me boxing tips, new workouts, and meetup invites by email. Unsubscribe anytime.
        </span>
      </label>
      <label className="flex items-start gap-2 mb-4 cursor-pointer">
        <input type="checkbox" required checked={form.terms_accepted} onChange={(e) => setForm({ ...form, terms_accepted: e.target.checked })}
          className="mt-0.5 h-4 w-4 accent-[#FF331F]" data-testid="ronin-auth-terms" />
        <span className="text-[11px] text-zinc-400 leading-snug">
          I accept the <a href="/terms" target="_blank" className="text-[#FF331F] underline">terms</a> and understand Ronin Box is for training only — train at my own risk.
        </span>
      </label>

      <Button type="submit" disabled={busy || postalState.status !== "ok"} className="w-full bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-[0.2em] text-xs py-6 rounded-sm disabled:opacity-50" data-testid="ronin-auth-submit">
        {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Creating account…</> : "Create free account"}
      </Button>
      <p className="text-center text-xs text-zinc-500 mt-3">
        Already have an account?{" "}
        <button type="button" onClick={() => setMode("login")} className="text-[#FF331F] font-bold uppercase tracking-widest" data-testid="ronin-auth-to-login">Log in</button>
      </p>
    </form>
  );
}

function LoginForm({ gate: g }) {
  const { loginEmail, setLoginEmail, loginPassword, setLoginPassword, busy, submitLogin, setMode } = g;
  return (
    <form onSubmit={submitLogin} data-testid="ronin-auth-login">
      <DialogHeader>
        <DialogTitle className="text-white uppercase tracking-tight">Welcome back</DialogTitle>
      </DialogHeader>
      <p className="text-xs text-zinc-400 mt-2 mb-4">Log in to unlock Ronin Box.</p>
      <div className="space-y-3">
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Email</Label>
          <Input required type="email" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} className="bg-[#050505] border-[#27272A] text-white rounded-sm" data-testid="ronin-login-email" />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label className="text-[10px] uppercase tracking-widest text-zinc-400 font-bold">Password</Label>
            <a
              href="/forgot-password"
              target="_blank" rel="noopener noreferrer"
              className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 hover:text-[#FF331F]"
              data-testid="ronin-login-forgot"
            >Forgot?</a>
          </div>
          <Input required type="password" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} className="bg-[#050505] border-[#27272A] text-white rounded-sm" data-testid="ronin-login-password" />
        </div>
      </div>
      <Button type="submit" disabled={busy} className="w-full mt-4 bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-[0.2em] text-xs py-6 rounded-sm" data-testid="ronin-login-submit">
        {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Logging in…</> : "Log in"}
      </Button>
      <button type="button" onClick={() => setMode("signup")} className="w-full text-center text-xs text-zinc-500 mt-3 hover:text-white inline-flex items-center justify-center gap-1" data-testid="ronin-auth-to-signup">
        <ArrowLeft className="h-3 w-3" /> Need an account? Sign up free
      </button>
    </form>
  );
}
